import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import SeoHead from '../components/SeoHead'
import GalleryLightbox from '../components/GalleryLightbox'
import { getCmsPage, getCmsGallery, getCmsCollection } from '../lib/cms'
import { getOptimizedImageUrl } from '../lib/utils'

export default function HifiPage() {
  const [pageData, setPageData] = useState(null)
  const [galleryData, setGalleryData] = useState({ gallery: null, items: [] })
  const [hifiItems, setHifiItems] = useState([])

  useEffect(() => {
    let isMounted = true
    Promise.all([
      getCmsPage('hifi'),
      getCmsGallery('hifi-setup'),
      getCmsCollection('hifi_items')
    ]).then(([page, gallery, items]) => {
      if (isMounted) {
        setPageData(page)
        setGalleryData(gallery)
        setHifiItems(items || [])
      }
    })
    return () => {
      isMounted = false
    }
  }, [])

  return (
    <div className="bg-[#121214] text-[#F5F3EF]">
      {/* 1:1 Exact Scrape Title and Metadata */}
      <SeoHead
        title={pageData?.seo_title || "HiFi & JARC Audio | High-End Lydoplevelser & Højttalere - JARCon"}
        description={
          pageData?.meta_description ||
          "Udforsk JARC Audio universet. Passion for DIY HiFi, håndbyggede højttalere, rørforstærkere og pladespillere. Kom forbi til en uforpligtende lytteoplevelse i Sydals."
        }
        ogImage="https://assets.auxodev.dk/assets/d6e713fb-6411-4075-ab6a-5936b7a50d5a/2dee4a-10d0de04f8d84061b64ec76601f0ef3c-mv2.jpg"
        canonicalUrl="https://www.jarcon.dk/hifi"
      />

      {/* HI-FI HEADER SECTION */}
      <section className="relative border-b border-white/10 bg-[#0E0E10] py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#C87D46]/40 bg-[#18181C] px-3.5 py-1 text-xs uppercase tracking-widest text-[#C87D46] font-medium mb-6">
            JARC Audio · HiFi Univers
          </div>

          {/* Scrape H1: HiFi */}
          <h1 className="font-serif text-4xl sm:text-6xl md:text-7xl text-[#F5F3EF]">
            HiFi
          </h1>

          <p className="mt-4 font-serif italic text-lg sm:text-2xl text-[#E5E2DB]">
            “Musik med nerve”
          </p>
        </div>
      </section>

      {/* VERBATIM PASSION & STATEMENT PROSE FROM SCRAPE */}
      <section className="py-16 sm:py-20 border-b border-white/10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-sm border border-white/10 bg-[#18181C] p-6 sm:p-10 md:p-12 space-y-6">
            {/* Scrape verbatim statements */}
            <div className="border-l-2 border-[#C87D46] pl-5 sm:pl-6 space-y-4">
              <h2 className="font-serif text-xl sm:text-2xl text-[#F5F3EF] leading-snug font-normal">
                HiFi og DIY HiFi har været en passion og hobby for mig i mange år.
              </h2>
              <h2 className="font-serif text-lg sm:text-xl text-[#E5E2DB] leading-snug font-normal">
                Jeg har lavet pladespillere, kabler og tilbehør, men primært højtalerbyg, har min interesse
              </h2>
              <h2 className="font-serif text-lg sm:text-xl text-[#E5E2DB] leading-snug font-normal">
                Det er blevet til mange spændende højtaler konstruktioner gennem årene.
              </h2>
            </div>

            <div className="pt-4 border-t border-white/5 space-y-4 text-sm sm:text-base text-[#F5F3EF] leading-relaxed">
              {/* Highlighted goal statement verbatim from scrape */}
              <div className="rounded-sm bg-[#121214] border border-[#C87D46]/30 p-5 sm:p-6">
                <p className="font-serif italic text-base sm:text-lg text-[#F5F3EF] leading-relaxed">
                  “Målet er at skabe en højtaler, som kan gengive al slags musik med nerve og nærvær, hvor lytteren glemmer at man lytter til HiFi, men føler sig omslugt at musikken.”
                </p>
              </div>

              {/* Scrape invitation verbatim */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
                <p className="text-sm sm:text-base font-medium text-[#E5E2DB]">
                  Du er velkommen til at komme forbi og lytte til mit setup med DIY højtalere, pladespiller og rørforstærker
                </p>
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 rounded-sm bg-[#C87D46] px-5 py-2.5 text-xs font-medium text-white shadow-lg shadow-[#C87D46]/20 hover:bg-[#D98E58] transition-colors whitespace-nowrap"
                >
                  <i className="fa-solid fa-mug-hot text-xs" aria-hidden="true" />
                  <span>Kom forbi til en kop kaffe</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DYNAMIC CMS GALLERY WITH LIGHTBOX (All scrape images + verbatim captions) */}
      <section id="setup-og-galleri" className="py-16 sm:py-24 bg-[#0E0E10] border-b border-white/10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <span className="text-xs uppercase tracking-widest text-[#C87D46] font-medium block mb-2">
              Lytterum & Komponenter
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#F5F3EF]">
              Mit Anlæg & Lytteposition
            </h2>
            <p className="mt-2 text-sm text-[#A8A49C]">
              Billeder og beskrivelser direkte fra lytterummet. Klik for at forstørre i fuld skærm.
            </p>
          </div>

          <GalleryLightbox
            items={galleryData.items}
            title={galleryData.gallery?.title || "Jens' HiFi Setup & Lytterum"}
          />
        </div>
      </section>

      {/* VERBATIM OFFERINGS & PRODUCTS FROM SCRAPE */}
      <section className="py-16 sm:py-24 border-b border-white/10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <span className="text-xs uppercase tracking-widest text-[#C87D46] font-medium block mb-2">
              Kompetencer & Forhandling
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#F5F3EF]">
              Højtalerbyg, Målesystemer & Forstærkere
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* 1. Højtaler udvikling */}
            <div className="rounded-sm border border-white/10 bg-[#18181C] p-6 sm:p-8 flex flex-col justify-between hover:border-[#C87D46]/40 transition-colors">
              <div>
                <h3 className="font-serif text-2xl text-[#F5F3EF] mb-4">
                  Højtaler udvikling
                </h3>
                <div className="space-y-4 text-sm text-[#A8A49C] leading-relaxed">
                  <p>
                    Da jeg gerne vil dele ud af min erfaring, tilbyder jeg at bygge højtalere på bestilling, men også hvis du har brug for inspiration til dit eget projekt.
                  </p>
                  <p className="text-[#E5E2DB] font-medium">
                    Ring eller skriv for at få en dialog om ønsker og muligheder for at skabe din helt egen højtaler
                  </p>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-white/5">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 text-xs font-medium text-[#C87D46] hover:text-[#D98E58] transition-colors"
                >
                  <span>Start en uforpligtende dialog</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* 2. Højtaler udmåling */}
            <div className="rounded-sm border border-white/10 bg-[#18181C] p-6 sm:p-8 flex flex-col justify-between hover:border-[#C87D46]/40 transition-colors">
              <div>
                <h3 className="font-serif text-2xl text-[#F5F3EF] mb-4">
                  Højtaler udmåling
                </h3>
                <div className="space-y-4 text-sm text-[#A8A49C] leading-relaxed">
                  <p>
                    Jeg tilbyder udmåling af dine højtalere med CLIO pocket målesystem:
                  </p>
                  <ul className="space-y-1.5 text-xs text-[#E5E2DB]">
                    <li className="flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#C87D46]" />
                      <span>Frekvensgang</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#C87D46]" />
                      <span>Vandfaldskurve</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#C87D46]" />
                      <span>TS parametre m.m.</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-white/5">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 text-xs font-medium text-[#C87D46] hover:text-[#D98E58] transition-colors"
                >
                  <span>Book udmåling af højtalere</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* 3. Forhandling af Hypex Elektronik */}
            <div className="rounded-sm border border-white/10 bg-[#18181C] p-6 sm:p-8 flex flex-col justify-between hover:border-[#C87D46]/40 transition-colors">
              <div>
                <h3 className="font-serif text-2xl text-[#F5F3EF] mb-4">
                  Forhandling af Hypex Elektronik
                </h3>
                <p className="text-sm text-[#A8A49C] leading-relaxed">
                  Forhandling af Hypex Class D forstærkere til fx aktive højtalere, samt DSP og forsyninger, alle i meget høj kvalitet
                </p>
              </div>

              <div className="mt-8 pt-6 border-t border-white/5">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 text-xs font-medium text-[#C87D46] hover:text-[#D98E58] transition-colors"
                >
                  <span>Hør nærmere om Hypex moduler</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-20 bg-[#0E0E10] text-center">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-serif text-3xl sm:text-4xl text-[#F5F3EF]">
              Oplev musikken med nerve og nærvær
            </h2>
            <p className="mt-4 text-sm text-[#A8A49C] leading-relaxed">
              Vi byder gerne velkommen i lytterummet på Toftehøj 21, 6470 Sydals. Kontakt Jens for at aftale en tid, der passer dig.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 rounded-sm bg-[#C87D46] px-8 py-3.5 text-sm font-medium text-white hover:bg-[#D98E58] transition-colors"
              >
                <i className="fa-solid fa-mug-hot text-xs" aria-hidden="true" />
                <span>Aftal et lyt</span>
              </Link>
              <a
                href="tel:+4522884536"
                className="inline-flex items-center gap-2 rounded-sm border border-white/20 px-8 py-3.5 text-sm font-medium text-[#F5F3EF] hover:bg-white/5 transition-colors whitespace-nowrap"
              >
                <i className="fa-solid fa-phone text-[#C87D46] text-xs" aria-hidden="true" />
                <span className="whitespace-nowrap">+45 22 88 45 36</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
